// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import React, {useMemo} from 'react';
import cx from 'classnames';
import type {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import Divider from './Divider.react';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {MovableItem} from '../../shared/MovableItem.react';
import {
  getSmallSupplement,
  SMALL_SUPPLEMENT_WRAPPER_CLASS,
  SMALL_SUPPLEMENT_WRAPPER_WITH_GAP_CLASS
} from '../../shared/DraggableChip';
import {itemRegionIdFor} from '../../shared/itemRegionId';
import {isItemCorrectAt} from './gradedOrder';

interface Props {
  item: {id: string; value: string; placedInSlot?: string | null};
  index: number;
  /** The spoken name of the divider before this item. */
  dividerLabel: string;
  /** Whether an item with the same value as the slot's answer counts as correct. */
  interchangeable: boolean;
  handleItemClick: (itemId: string, event: React.SyntheticEvent) => void;
  handleDividerClick: (dividerIndex: number) => void;
  question: QuestionModelV3;
  /** `li` in the answer's list; `div` in the graded table, whose cell is already the list item. */
  as?: 'div' | 'li';
  /** The gap after the last item. It sits in that item's list entry, so the list counts items, not gaps. */
  trailingDivider?: React.ReactNode;
}

export const Item = ({
  item,
  dividerLabel,
  interchangeable,
  handleItemClick,
  handleDividerClick,
  index,
  question,
  as: Root = 'div',
  trailingDivider
}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);
  const isSupplement = !!getSmallSupplement(item.value);
  const supplementWrapperClass = trailingDivider
    ? SMALL_SUPPLEMENT_WRAPPER_WITH_GAP_CLASS
    : SMALL_SUPPLEMENT_WRAPPER_CLASS;

  const {activeItemId, isDragging, hoveredDividerId, draggedItemId, isSubmitted, correctAnswer} =
    // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
    useStoreHook(
      useShallow((state) => ({
        activeItemId: state.activeItemId,
        isDragging: state.isDragging,
        hoveredDividerId: state.hoveredDividerId,
        draggedItemId: state.draggedItemId,
        isSubmitted: state.isSubmitted,
        correctAnswer: state.correctAnswer
      }))
    );

  const isCorrect = useMemo(
    () =>
      isItemCorrectAt(
        question,
        correctAnswer,
        {id: item.id, value: item.value},
        index,
        interchangeable
      ),
    [correctAnswer, item.id, item.value, index, question, interchangeable]
  );

  return (
    <Root
      key={`${question?.getId?.()}-${item.id}`}
      className={cx(
        'flex items-stretch',
        isSupplement ? supplementWrapperClass : 'max-w-full min-w-x0'
      )}
    >
      {!isSubmitted && (
        <Divider
          id={`divider-${index}`}
          label={dividerLabel}
          isVisible={isDragging || activeItemId !== null}
          isHovered={hoveredDividerId === `divider-${index}`}
          onClick={() => handleDividerClick(index)}
        />
      )}
      <div className={cx('max-w-full min-w-x0', {'flex-1': isSupplement})}>
        <MovableItem
          key={item.id}
          item={item}
          isActive={activeItemId === item.id}
          isDragging={isDragging && draggedItemId === item.id}
          isSubmitted={isSubmitted}
          onClick={(e) => handleItemClick(item.id, e)}
          activeItemId={activeItemId}
          isCorrect={isCorrect}
          regionId={itemRegionIdFor(question?.getId?.(), item)}
          hasMinTargetWidth
        />
      </div>
      {trailingDivider}
    </Root>
  );
};
